import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PREVIEWS } from "@/data/book";
import { priceCta } from "@/config/site";
import { trackEvent } from "@/lib/analytics";
import { CtaButton } from "./CtaButton";
import { Reveal } from "./Reveal";

export const Previews = () => {
  const [active, setActive] = useState(null);

  const openPreview = (item) => {
    setActive(item);
    trackEvent("principle_preview_open", { principle: item.number });
  };

  return (
    <section id="ukazky" className="grain relative bg-navy-deep py-28 sm:py-36" data-testid="previews-section">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 z-[2] h-48 bg-[radial-gradient(70%_100%_at_50%_0%,rgba(201,127,82,0.12),transparent_70%)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-48 bg-[radial-gradient(70%_100%_at_50%_100%,rgba(166,90,58,0.10),transparent_70%)]"
      />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="mb-6 flex items-center gap-4 text-xs font-semibold uppercase tracking-[0.3em] text-copper">
            <span className="h-px w-10 bg-copper/60" aria-hidden="true" />
            Ukázky z knihy
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl">
            Nahlédni do <span className="italic text-copper">40 principů</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-cream/60 sm:text-lg">
            Žádné univerzální poučky. Každý princip rozebírá jednu konkrétní oblast života a
            převádí ji do situací, které možná znáš i ty.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {PREVIEWS.map((item, i) => (
            <Reveal key={item.number} delay={0.05 * (i % 3)}>
              <article className="group flex h-full flex-col rounded-lg border border-white/[0.08] bg-navy p-8 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-copper/40">
                <span className="text-xs font-semibold uppercase tracking-[0.25em] text-copper/80">
                  Princip {item.number}
                </span>
                <h3 className="mt-4 font-serif text-2xl font-medium leading-snug text-cream">
                  {item.title}
                </h3>
                <p className="mt-4 flex-1 text-[15px] leading-relaxed text-cream/60">{item.teaser}</p>
                <button
                  type="button"
                  onClick={() => openPreview(item)}
                  data-testid={`preview-open-${item.number}`}
                  className="mt-8 inline-flex items-center gap-2 self-start text-sm font-semibold uppercase tracking-wider text-copper transition-colors hover:text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-copper"
                >
                  Přečíst ukázku
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                </button>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14 text-center">
          <CtaButton event="previews_cta" location="previews" size="lg" testId="previews-cta">
            {priceCta}
          </CtaButton>
        </Reveal>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="preview-modal"
          className="max-h-[85vh] max-w-2xl overflow-y-auto border-white/10 bg-navy-deep text-cream sm:rounded-lg"
        >
          {active && (
            <>
              <DialogHeader>
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-copper/80">
                  Ukázka · Princip {active.number}
                </p>
                <DialogTitle className="pt-2 text-left font-serif text-3xl font-medium leading-snug text-cream">
                  {active.title}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Ukázka textu principu {active.number} z e-booku 40 principů lepšího muže
                </DialogDescription>
              </DialogHeader>

              <div className="relative mt-6">
                <div className="space-y-5">
                  {active.excerpt.map((block, i) =>
                    block.type === "h" ? (
                      <h4 key={i} className="pt-4 font-serif text-xl font-medium text-copper">
                        {block.text}
                      </h4>
                    ) : (
                      <p key={i} className="text-[15px] leading-[1.85] text-cream/85 sm:text-base">
                        {block.text}
                      </p>
                    )
                  )}
                </div>
                <div
                  aria-hidden="true"
                  className="pointer-events-none sticky bottom-0 -mb-6 -mx-6 h-32 bg-gradient-to-t from-navy-deep via-navy-deep/85 to-transparent"
                />
              </div>

              <div className="relative mt-2 border-t border-white/10 pt-8 text-center">
                <p className="font-serif text-lg italic text-cream/80">
                  Pokračování najdeš v e-booku
                </p>
                <div className="mt-5 flex justify-center">
                  <CtaButton
                    event="principle_preview_cta"
                    location={`preview-${active.number}`}
                    size="md"
                    testId="preview-modal-cta"
                  >
                    Chci celý e-book za 159 Kč
                  </CtaButton>
                </div>
                <p className="sr-only">{priceCta}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
